import { diffBetweenObjects } from '@trip-ux/common';
import { assign, filter, find, flatMap, get, includes, intersection, isEmpty, map, negate, reduce } from 'lodash';
import { PolicyRequest } from '../policy-create';
import { PolicySearchResponse } from '../policy-search';
import {
  POLICY_INFO,
  POLICY_INFO_SUCCESS,
  POLICY_INFO_FAILED,
  POLICY_INFO_EXPIRED,
  POLICY_INFO_SELECT,
  POLICY_INFO_UNSET,
  PolicyInfoList,
  POLICY_INFO_NAV_PAGE,
  POLICY_INFO_EDIT,
  POLICY_EDIT_ERRORS,
  POLICY_EDIT_VALUES,
  ACTIVE_ACCORDION,
  POLICY_RESEND_SUCCESS,
  POLICY_RESEND_FAILED,
  POLICY_RESEND_STARTED,
  POLICY_INITIAL_VALUES,
  REMOVE_DEPENDENT,
  DEPENDENT_EDIT_ERRORS,
  DEPENDENT_EDIT_VALUES,
  DEPENDENT_RESET_VALUES,
  DEPENDENT_INITIAL_VALUES,
  POLICY_PREMIUM_CHANGED,
  POLICY_LAYOVER_REDIRECT
} from './types';
import { Dependent } from '../quote';

export const evaluateFamilyDelta = (info: PolicyInfoList) => {
  const editRefs = flatMap(info.dependentsChanges, 'referenceNumber');
  const initialRefs = flatMap(info.dependentsInitials, 'referenceNumber');
  const removed = filter(info.dependentsInitials, dep => !includes(editRefs, dep.referenceNumber));
  const added = filter(info.dependentsChanges, dep => !includes(initialRefs, dep.referenceNumber));
  const changed = reduce(
    intersection(editRefs, initialRefs),
    (res, referenceNumber) => {
      const delta = diffBetweenObjects(
        find(info.dependentsChanges, { referenceNumber }),
        find(info.dependentsInitials, { referenceNumber })
      );
      return isEmpty(delta) ? res : [...res, delta];
    },
    []
  );
  const hasError = find(info.dependentsErrors, negate(isEmpty));
  const hasChanges = !isEmpty(removed) || !isEmpty(added) || !isEmpty(changed);
  return { removed, added, changed, hasChanges, hasError };
};

const REF_NUM = 'referenceNumber';
export const policyInfo = (
  state: PolicyInfoList = {
    searchFetched: false
  },
  action: {
    type: string;
    payload?: unknown;
  }
): PolicyInfoList => {
  switch (action.type) {
    case POLICY_INFO:
      return {
        ...state,
        searchFetched: false,
        searchExpired: false,
        searchError: null,
        resendPending: null,
        resendError: null,
        resendDone: null,
        premiumChanged: false,
        layoverRedirect: false
      };
    case POLICY_INFO_SUCCESS: {
      return {
        ...state,
        searchFetched: true,
        data: action.payload as PolicySearchResponse
      };
    }
    case POLICY_INFO_FAILED:
      return { ...state, searchFetched: true, searchError: action.payload };
    case POLICY_INFO_EXPIRED:
      return { ...state, searchFetched: true, searchExpired: true };
    case POLICY_INFO_UNSET:
      return { searchFetched: false };
    case POLICY_PREMIUM_CHANGED:
      return { ...state, premiumChanged: action.payload as boolean };
    case POLICY_LAYOVER_REDIRECT:
      return { ...state, layoverRedirect: action.payload as boolean };
    case POLICY_INFO_SELECT:
      return {
        ...state,
        editPolicies: false,
        activeAccordion: 0,
        selectedPolicies: action.payload as PolicyRequest[],
        resendPending: null,
        resendError: null,
        resendDone: null
      };
    case POLICY_INFO_NAV_PAGE:
      return { ...state };
    case POLICY_INFO_EDIT:
      return { ...state, editPolicies: action.payload as boolean };
    case POLICY_INITIAL_VALUES:
      return { ...state, initialValues: assign(state.initialValues, action.payload) };
    case POLICY_EDIT_ERRORS:
      return { ...state, editErrors: assign(state.editErrors, action.payload) };
    case POLICY_EDIT_VALUES: {
      const editChanges = assign(state.editChanges, action.payload);
      const deltaChanges = diffBetweenObjects(editChanges, get(state, 'initialValues', {}));
      return { ...state, editChanges, deltaChanges };
    }
    case ACTIVE_ACCORDION:
      return { ...state, activeAccordion: action.payload as number };
    case POLICY_RESEND_STARTED:
      return { ...state, resendPending: true, resendError: null, resendDone: false };
    case POLICY_RESEND_SUCCESS:
      return { ...state, resendPending: false, resendError: null, resendDone: true };
    case POLICY_RESEND_FAILED:
      return { ...state, resendPending: false, resendError: action.payload, resendDone: false };
    case DEPENDENT_INITIAL_VALUES:
      return {
        ...state,
        dependentsErrors: [],
        dependentsInitials: action.payload as Dependent[],
        dependentDelta: {}
      };
    case DEPENDENT_RESET_VALUES:
      return {
        ...state,
        dependentsErrors: [],
        dependentsChanges: state.dependentsInitials ? [...state.dependentsInitials] : [],
        dependentDelta: {}
      };
    case DEPENDENT_EDIT_ERRORS: {
      const dependentsErrors = filter(
        state.dependentsErrors,
        val => get(val, REF_NUM) !== get(action.payload, REF_NUM)
      );
      dependentsErrors.push(action.payload as Record<string, string>);
      return { ...state, dependentsErrors };
    }
    case DEPENDENT_EDIT_VALUES: {
      const dependentsChanges = filter(
        state.dependentsChanges,
        val => get(val, REF_NUM) !== get(action.payload, REF_NUM)
      );
      dependentsChanges.push(action.payload as Dependent);
      const newState = { ...state, dependentsChanges };
      const dependentDelta = evaluateFamilyDelta(newState);
      return { ...newState, dependentDelta };
    }
    case REMOVE_DEPENDENT: {
      const dependentsChanges = filter(state.dependentsChanges, val => get(val, REF_NUM) !== action.payload);
      const dependentsErrors = filter(state.dependentsErrors, val => get(val, REF_NUM) !== action.payload);
      const newState = { ...state, dependentsChanges, dependentsErrors };
      const dependentDelta = evaluateFamilyDelta(newState);
      return { ...newState, dependentDelta };
    }
    default:
      return state;
  }
};
